import { useEffect, useMemo, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  TextInput,
  FlatList,
  Text,
  View,
  Pressable,
  Image,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import type { RootStackParamList } from "../../navigation/main";
import type { ColumnVideo } from "../columns";
import { fetchAllColumnVideos } from "../../services/videoServer";

export default function SearchScreen() {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [query, setQuery] = useState("");
  const [videos, setVideos] = useState<ColumnVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    fetchAllColumnVideos()
      .then((items) => {
        if (active) setVideos(items);
      })
      .catch(() => {
        if (active) setError("加载失败，点击重试");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [retry]);
  const results = useMemo(() => {
    const terms = query
      .normalize("NFKC")
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!terms.length) return [];
    return videos.filter((item) => {
      const text = [item.title, item.channel, item.meta, ...(item.tags || [])]
        .join(" ")
        .normalize("NFKC")
        .toLowerCase();
      return terms.every((term) => text.includes(term));
    });
  }, [query, videos]);
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#06110e" }}>
      <TextInput
        value={query}
        onChangeText={setQuery}
        placeholder="搜索视频、频道或标签"
        accessibilityLabel="搜索视频"
        placeholderTextColor="#8b9692"
        returnKeyType="search"
        style={{
          margin: 12,
          padding: 12,
          borderRadius: 8,
          backgroundColor: "#0b211a",
          color: "white",
        }}
      />
      {loading ? (
        <ActivityIndicator color="#28c99a" />
      ) : error ? (
        <Pressable onPress={() => setRetry((value) => value + 1)}>
          <Text style={{ color: "#ddd", padding: 16 }}>{error}</Text>
        </Pressable>
      ) : (
        <FlatList
          data={results}
          keyExtractor={(item) => item.id}
          keyboardShouldPersistTaps="handled"
          ListHeaderComponent={
            <Text style={{ color: "#8b9692", padding: 12 }}>
              {query.trim()
                ? `找到 ${results.length} 个视频`
                : "输入关键词搜索视频"}
            </Text>
          }
          ListEmptyComponent={
            query.trim() ? (
              <Text style={{ color: "#8b9692", padding: 12 }}>
                没有找到相关视频，换个关键词试试
              </Text>
            ) : null
          }
          renderItem={({ item, index }) => (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`播放 ${item.title}`}
              onPress={() =>
                navigation.navigate("columnVideo", {
                  videos: results,
                  initialIndex: index,
                })
              }
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                padding: 12,
              }}
            >
              <Image
                source={{ uri: item.thumbnail }}
                style={{ width: 120, height: 68, borderRadius: 8 }}
              />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  numberOfLines={2}
                  style={{ color: "white", fontSize: 14 }}
                >
                  {item.title}
                </Text>
                <Text
                  numberOfLines={1}
                  style={{ color: "#8b9692", fontSize: 12, marginTop: 6 }}
                >
                  {item.channel} · {item.duration}
                </Text>
              </View>
            </Pressable>
          )}
        />
      )}
    </SafeAreaView>
  );
}
